import React, { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { SocialPost, SocialPlatform } from '../../../../types/socialMediaPlanner';
import { SocialMediaPlannerService } from '../../../../services/socialMediaPlannerService';
import { socialMediaContentService } from '../../../../services/socialMediaContentService';
import { urlShortenerService } from '../../../../services/urlShortenerService';
import ClaudeContentModal from './ClaudeContentModal';
import HashtagCollectionModal from '../../../../components/social/HashtagCollectionModal';
import toast from 'react-hot-toast';

// Define PlatformPostDetail interface if not already in types file
interface PlatformPostDetail {
  platform_specific_content: string;
  scheduled_for?: string;
}

interface PostComposerProps {
  post?: SocialPost;
  onSave: (post: SocialPost) => void;
  onClose: () => void;
}

/**
 * PostComposer Component
 * 
 * A component for creating and editing social media posts with platform-specific content.
 * Uses Tailwind CSS exclusively for styling, following project standards.
 */
const PostComposer: React.FC<PostComposerProps> = ({ post, onSave, onClose }) => {
  // Base form state with default values
  const defaultFormState: SocialPost = {
    content: '',
    platforms: [] as SocialPlatform[], // Use SocialPlatform[] instead of string[]
    hashtags: [] as string[],
    status: 'draft',
    media_urls: [] as string[]
  };

  // Platform-specific content state
  const [platformSpecificContent, setPlatformSpecificContent] = useState<Record<string, string>>({});
  
  // References for text areas
  const commonTextareaRef = useRef<HTMLTextAreaElement>(null);
  const platformTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Component state
  const [formState, setFormState] = useState<SocialPost>(defaultFormState);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [scheduleDate, setScheduleDate] = useState<string>('');
  const [scheduleTime, setScheduleTime] = useState<string>('');
  const [isScheduled, setIsScheduled] = useState<boolean>(false);
  const [hashtag, setHashtag] = useState<string>('');
  const [showHashtagOptions, setShowHashtagOptions] = useState<boolean>(false);
  const [showColorPicker, setShowColorPicker] = useState<boolean>(false);
  const [showHashtagModal, setShowHashtagModal] = useState<boolean>(false);
  const [showClaudeModal, setShowClaudeModal] = useState<boolean>(false);
  
  // Platform preview state
  const [activePreviewPlatform, setActivePreviewPlatform] = useState<SocialPlatform | null>(null);
  
  // URL shortening functions
  const validateUrl = (url: string): boolean => {
    // Ensure URL has protocol
    let urlToValidate = url.trim();
    if (!urlToValidate.match(/^(http:\/\/|https:\/\/)/i)) {
      urlToValidate = `https://${urlToValidate}`;
    }
    
    // Use a more comprehensive URL validation regex
    const urlPattern = /^(https?:\/\/)([\da-z.-]+)\.([a-z.]{2,})([/\w.-]*)*\/?$/;
    return urlPattern.test(urlToValidate);
  };
  
  const handleShortenUrl = async () => {
    setUrlError('');
    
    if (!urlToShorten.trim()) {
      setUrlError('Please enter a URL to shorten');
      return;
    }
    
    // Ensure URL has protocol
    let finalUrl = urlToShorten.trim();
    if (!finalUrl.match(/^(http:\/\/|https:\/\/)/i)) {
      finalUrl = `https://${finalUrl}`;
      setUrlToShorten(finalUrl);
    }
    
    // Validate URL format
    if (!validateUrl(finalUrl)) {
      setUrlError('Invalid URL format. Please enter a valid URL (e.g., example.com or https://example.com)');
      return;
    }
    
    try {
      setShorteningUrl(true);
      const result = await urlShortenerService.shortenUrl({
        original_url: finalUrl,
        custom_code: '', // No custom code
        title: 'Post URL' // Generic title
      });
      
      if (result) {
        const shortUrlPath = `https://explanatum.co.uk/s/${result.short_code}`;
        setShortUrl(shortUrlPath);
        setShortenedUrls([...shortenedUrls, {
          original: finalUrl,
          shortened: shortUrlPath
        }]);
        toast.success('URL shortened successfully!');
        setUrlToShorten('');
      }
    } catch (error: any) {
      console.error('Error shortening URL:', error);
      
      // Extract meaningful error message if possible
      let errorMessage = 'Failed to shorten URL';
      
      if (error?.message) {
        errorMessage = error.message;
      }
      
      // Extract API error message if available
      if (error?.userMessage) {
        errorMessage = error.userMessage;
      } else if (error?.details?.message) {
        errorMessage = error.details.message;
      }
      
      // Set URL error for inline display
      setUrlError(errorMessage);
      
      // Also show toast for visibility
      toast.error(errorMessage);
    } finally {
      setShorteningUrl(false);
    }
  };
  
  const insertShortUrl = (url: string) => {
    // Get the appropriate textarea based on current view
    const textArea = showPlatformPreviews && activePreviewPlatform 
      ? platformTextareaRef.current 
      : commonTextareaRef.current;
      
    if (!textArea) return;
    
    const start = textArea.selectionStart;
    const end = textArea.selectionEnd;
    const updatedValue = textArea.value.substring(0, start) + url + textArea.value.substring(end);
    
    if (showPlatformPreviews && activePreviewPlatform) {
      // Update platform-specific content
      setPlatformSpecificContent({
        ...platformSpecificContent,
        [activePreviewPlatform]: updatedValue
      });
    } else {
      // Update common content
      setFormState({
        ...formState,
        content: updatedValue
      });
    }
    
    // Reset cursor position after insertion
    setTimeout(() => {
      if (textArea) {
        textArea.focus();
        const newPosition = start + url.length;
        textArea.setSelectionRange(newPosition, newPosition);
      }
    }, 0);
  };
  const [showPlatformPreviews, setShowPlatformPreviews] = useState<boolean>(false);
  const [selectedText, setSelectedText] = useState<{start: number; end: number; text: string} | null>(null);
  
  // URL shortening states
  const [urlToShorten, setUrlToShorten] = useState<string>('');
  const [shorteningUrl, setShorteningUrl] = useState<boolean>(false);
  const [shortUrl, setShortUrl] = useState<string>('');
  const [shortenedUrls, setShortenedUrls] = useState<{original: string; shortened: string}[]>([]);
  const [urlError, setUrlError] = useState<string>('');
  
  // Formatting helpers
  const applyFormatting = (format: 'bold' | 'italic' | 'hashtag'): void => {
    const textArea = showPlatformPreviews && activePreviewPlatform 
      ? platformTextareaRef.current 
      : commonTextareaRef.current;
    
    if (!textArea) return;
    
    const start = textArea.selectionStart;
    const end = textArea.selectionEnd;
    const selectedContent = textArea.value.substring(start, end);
    
    if (start === end && format !== 'hashtag') {
      // No text selected, do nothing for bold/italic
      return;
    }
    
    let newText = '';
    let newCursorPosition = start;
    
    switch (format) {
      case 'bold':
        newText = `**${selectedContent}**`;
        newCursorPosition = start + 2 + selectedContent.length;
        break;
      case 'italic':
        newText = `*${selectedContent}*`;
        newCursorPosition = start + 1 + selectedContent.length;
        break;
      case 'hashtag':
        // For hashtag, insert # at cursor or at start of selection
        if (start === end) {
          newText = '#';
          newCursorPosition = start + 1;
        } else {
          newText = `#${selectedContent}`;
          newCursorPosition = start + 1 + selectedContent.length;
        }
        break;
    }
    
    // Apply the formatting
    const updatedValue = textArea.value.substring(0, start) + newText + textArea.value.substring(end);
    
    if (showPlatformPreviews && activePreviewPlatform) {
      // Update platform-specific content
      setPlatformSpecificContent({
        ...platformSpecificContent,
        [activePreviewPlatform]: updatedValue
      });
    } else {
      // Update common content
      setFormState({
        ...formState,
        content: updatedValue
      });
    }
    
    // Reset cursor position after state update
    setTimeout(() => {
      if (textArea) {
        textArea.focus();
        textArea.setSelectionRange(newCursorPosition, newCursorPosition);
      }
    }, 0);
  };
  
  // Predefined color options for quick selection
  const colorOptions = [
    '#3B82F6', // Blue
    '#10B981', // Green
    '#F59E0B', // Yellow/Amber
    '#EF4444', // Red
    '#8B5CF6', // Purple
    '#EC4899', // Pink
    '#6B7280', // Gray
    '#000000', // Black
  ];

  // Service initialization - defined once to prevent duplicate declarations
  const socialMediaPlannerService = new SocialMediaPlannerService();
  
  // Get platform-specific character limit information
  const getPlatformCharacterLimit = (platform: SocialPlatform, content: string): string => {
    const limits: Record<SocialPlatform, number> = {
      twitter: 280,
      facebook: 63206,
      instagram: 2200,
      linkedin: 3000,
      pinterest: 500,
      tiktok: 2200,
      youtube: 5000
    };

    const limit = limits[platform] || 280;
    const remaining = limit - content.length;

    if (remaining < 0) {
      return `${Math.abs(remaining)} characters over ${platform} limit`;
    }

    return `${content.length}/${limit} characters${remaining < 20 ? ' (limit approaching)' : ''}`;
  };

  // Load post data if editing
  useEffect(() => {
    if (post) {
      setFormState({
        ...post,
        // Ensure we have default values for optional fields
        platforms: post.platforms || [],
        hashtags: post.hashtags || [],
        media_urls: post.media_urls || []
      });

      // Set platform specific content
      if (post.platform_details && post.platform_details.length > 0) {
        const platformContent: Record<SocialPlatform, string> = {
          facebook: '',
          twitter: '',
          linkedin: '',
          instagram: '',
          pinterest: '',
          tiktok: '',
          youtube: ''
        };

        post.platform_details.forEach(detail => {
          if (detail.platform_specific_content) {
            platformContent[detail.platform] = detail.platform_specific_content;
          }
        });

        setPlatformSpecificContent(platformContent);
      }

      // Set scheduling if applicable
      if (post.scheduled_for) {
        setIsScheduled(true);
        const date = new Date(post.scheduled_for);
        setScheduleDate(format(date, 'yyyy-MM-dd'));
        setScheduleTime(format(date, 'HH:mm'));
      }
    }
  }, [post]);

  // Handle platform toggle
  const handlePlatformToggle = (platform: SocialPlatform) => {
    let updatedPlatforms = [...formState.platforms];
    
    if (updatedPlatforms.includes(platform)) {
      // Remove platform if already selected
      updatedPlatforms = updatedPlatforms.filter(p => p !== platform);
    } else {
      // Add platform if not already selected
      updatedPlatforms.push(platform);
    }
    
    setFormState({ ...formState, platforms: updatedPlatforms });
  };

  // Handle opening Claude modal for content generation
  const handleOpenClaudeModal = () => {
    if (formState.platforms.length === 0) {
      toast.error('Please select at least one platform before generating content');
      return;
    }
    setShowClaudeModal(true);
  };
  
  // Handle content generated from Claude
  const handleClaudeContentGenerated = (content: string, hashtags?: string[]) => {
    // If we're in platform-specific mode, update that platform's content
    if (showPlatformPreviews && activePreviewPlatform) {
      setPlatformSpecificContent({
        ...platformSpecificContent,
        [activePreviewPlatform]: content
      });
    } else {
      // Otherwise update the main content
      setFormState(prevState => ({ 
        ...prevState, 
        content 
      }));
      
      // If hashtags were provided, add them to the post
      if (hashtags && Array.isArray(hashtags) && hashtags.length > 0) {
        const updatedHashtags = [...(formState.hashtags || [])];
        hashtags.forEach(tag => {
          if (!updatedHashtags.includes(tag)) {
            updatedHashtags.push(tag);
          }
        });
        setFormState(prev => ({
          ...prev,
          hashtags: updatedHashtags
        }));
      }
    }
  };
  
  // AI content generation handled by Claude modal
  
  // Generate a recurrence summary for display
  const generateRecurrenceSummary = (): string => {
    return 'This post will not repeat.';
  };

  // Generate RFC 5545 iCalendar recurrence rule
  const generateRecurrenceRule = (): string | undefined => {
    return undefined;
  };

  // Add renderPlatformButton function
  const renderPlatformButton = (platform: SocialPlatform) => {
    const isSelected = formState.platforms.includes(platform);
    
    // Platform-specific styles
    const styles: Record<SocialPlatform, { selected: string, unselected: string }> = {
      facebook: {
        selected: 'bg-blue-600 text-white',
        unselected: 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      },
      twitter: {
        selected: 'bg-blue-400 text-white',
        unselected: 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      },
      linkedin: {
        selected: 'bg-blue-800 text-white',
        unselected: 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      },
      pinterest: {
        selected: 'bg-red-600 text-white',
        unselected: 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      },
      tiktok: {
        selected: 'bg-black text-white',
        unselected: 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      },
      instagram: {
        selected: 'bg-purple-600 text-white',
        unselected: 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      },
      youtube: {
        selected: 'bg-red-700 text-white',
        unselected: 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      }
    };
    
    const iconText: Record<string, string> = {
      facebook: 'f',
      twitter: 't',
      linkedin: 'in',
      instagram: 'ig',
      pinterest: 'p',
      tiktok: 'tt',
      youtube: 'yt'
    };
    
    return (
      <button
        type="button"
        onClick={() => handlePlatformToggle(platform)}
        className={`p-3 rounded-full ${isSelected ? styles[platform].selected : styles[platform].unselected} flex items-center justify-center w-10 h-10`}
      >
        <span className="font-bold text-sm">{iconText[platform]}</span>
      </button>
    );
  };

  // Handle submit
  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Validate required fields
      if (!formState.content.trim()) {
        setError('Please enter content for your post');
        setIsLoading(false);
        return;
      }

      if (formState.platforms.length === 0) {
        setError('Please select at least one platform');
        setIsLoading(false);
        return;
      }

      // Prepare the post data
      const postData: SocialPost = {
        ...formState,
        status: isScheduled ? 'scheduled' : 'draft'
      };

      // Add scheduled date if applicable
      if (isScheduled && scheduleDate && scheduleTime) {
        const scheduledDateTime = new Date(`${scheduleDate}T${scheduleTime}`);
        postData.scheduled_for = scheduledDateTime.toISOString();
      } else {
        postData.scheduled_for = undefined;
      }
      
      // Add platform-specific variations
      const platformVariations: Record<string, { platform_specific_content: string, scheduled_for?: string }> = {};
      
      // Process platform-specific content
      formState.platforms.forEach((platform) => {
        const platformContent = platformSpecificContent[platform];
        if (platformContent && platformContent.trim() && platformContent !== formState.content) {
          platformVariations[platform] = {
            platform_specific_content: platformContent,
            scheduled_for: postData.scheduled_for || undefined // Default to same schedule, ensure type safety
          };
        }
      });
      
      // Save the post
      onSave(postData);
      
    } catch (error) {
      console.error('Error saving post:', error);
      setError('Failed to save post');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">
            {post ? 'Edit Post' : 'Create New Post'}
          </h2>
          
          <button 
            onClick={onClose}
            className="rounded-full w-8 h-8 flex items-center justify-center bg-gray-200 hover:bg-gray-300"
            aria-label="Close"
          >
            <span aria-hidden="true" className="text-gray-600">&times;</span>
          </button>
        </div>
        
        {/* Platform Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Platforms
          </label>
          <div className="flex space-x-4">
            {renderPlatformButton('facebook')}
            {renderPlatformButton('twitter')}
            {renderPlatformButton('linkedin')}
            {renderPlatformButton('instagram')}
          </div>
          
          {/* Selected platforms display */}
          {formState.platforms.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {formState.platforms.map((platform) => (
                <div 
                  key={platform} 
                  className="flex items-center bg-gray-100 rounded px-2 py-1"
                >
                  <span className="capitalize mr-1">{platform}</span>
                  <button 
                    onClick={() => handlePlatformToggle(platform as SocialPlatform)}
                    className="ml-1 text-gray-500 hover:text-gray-700"
                    aria-label={`Remove ${platform}`}
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Color Picker */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Post Color
          </label>
          <div className="flex items-center">
            <div 
              className="w-8 h-8 rounded-md border border-gray-300 mr-3 cursor-pointer flex items-center justify-center"
              style={{ backgroundColor: formState.color || '#FFFFFF' }}
              onClick={() => setShowColorPicker(!showColorPicker)}
            >
              {!formState.color && <span className="text-gray-400">+</span>}
            </div>
            <span className="text-sm text-gray-500">
              {formState.color ? 'Click to change color' : 'Select a color for your post'}
            </span>
          </div>
          
          {showColorPicker && (
            <div className="mt-2 p-2 border border-gray-200 rounded-md bg-white shadow-sm">
              <div className="grid grid-cols-4 gap-2 mb-2">
                {colorOptions.map((color) => (
                  <div 
                    key={color}
                    className={`w-8 h-8 rounded-md cursor-pointer hover:scale-110 transition-transform ${formState.color === color ? 'ring-2 ring-blue-500' : ''}`}
                    style={{ backgroundColor: color }}
                    onClick={() => {
                      setFormState({ ...formState, color });
                      setShowColorPicker(false);
                    }}
                  />
                ))}
              </div>
              <div className="flex items-center mt-2">
                <input 
                  type="text" 
                  value={formState.color || ''}
                  onChange={(e) => setFormState({ ...formState, color: e.target.value })}
                  className="text-xs px-2 py-1 border border-gray-300 rounded w-24"
                  placeholder="#HEX code"
                />
                <button
                  type="button"
                  onClick={() => {
                    setFormState({ ...formState, color: undefined });
                    setShowColorPicker(false);
                  }}
                  className="ml-2 text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded hover:bg-gray-200"
                >
                  Clear
                </button>
              </div>
            </div>
          )}
        </div>
        
        {/* Hashtags Manager */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Hashtags
          </label>
          <div className="flex flex-col space-y-2">
            <div className="flex">
              <input
                type="text"
                value={hashtag}
                onChange={(e) => setHashtag(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && hashtag.trim()) {
                    e.preventDefault();
                    // Format hashtag properly
                    let newTag = hashtag.trim();
                    if (!newTag.startsWith('#')) {
                      newTag = `#${newTag}`;
                    }
                    
                    // Add hashtag if not already present
                    const currentHashtags = formState.hashtags || [];
                    if (!currentHashtags.includes(newTag)) {
                      setFormState({
                        ...formState,
                        hashtags: [...currentHashtags, newTag]
                      });
                    }
                    setHashtag('');
                  }
                }}
                className="flex-grow px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Add hashtag (e.g., #marketing)"
              />
              <button
                type="button"
                onClick={() => {
                  if (hashtag.trim()) {
                    // Format hashtag properly
                    let newTag = hashtag.trim();
                    if (!newTag.startsWith('#')) {
                      newTag = `#${newTag}`;
                    }
                    
                    // Add hashtag if not already present
                    const currentHashtags = formState.hashtags || [];
                    if (!currentHashtags.includes(newTag)) {
                      setFormState({
                        ...formState,
                        hashtags: [...currentHashtags, newTag]
                      });
                    }
                    setHashtag('');
                  }
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-r-md hover:bg-blue-700"
              >
                Add
              </button>
              <button
                type="button"
                onClick={() => setShowHashtagModal(true)}
                className="ml-2 px-4 py-2 border border-blue-300 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100"
                title="Open Hashtag Collections"
              >
                Collections
              </button>
            </div>
            
            {/* Display current hashtags with remove option */}
            {formState.hashtags && formState.hashtags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2 p-2 border border-gray-200 rounded-md bg-gray-50">
                {formState.hashtags.map((tag, index) => (
                  <span
                    key={index}
                    className="flex items-center bg-white border border-gray-300 rounded-full px-3 py-1 text-sm"
                  >
                    <span className="text-blue-600">{tag}</span>
                    <button
                      type="button"
                      onClick={() => {
                        const updatedHashtags = [...formState.hashtags || []];
                        updatedHashtags.splice(index, 1);
                        setFormState({
                          ...formState,
                          hashtags: updatedHashtags
                        });
                      }}
                      className="ml-2 text-gray-400 hover:text-red-500"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* URL Shortener */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-gray-700">
              URL Shortener
            </label>
          </div>
          <div className="text-sm text-gray-500 mb-2">
            Shorten URLs to track clicks and save character space in your posts.
          </div>
          
          <div className="flex">
            <input
              type="text"
              value={urlToShorten}
              onChange={(e) => {
                setUrlToShorten(e.target.value);
                setUrlError(''); // Clear error when user types
              }}
              placeholder="Enter URL to shorten (e.g., example.com)"
              className={`flex-grow px-3 py-2 border ${urlError ? 'border-red-500' : 'border-gray-300'} rounded-l-md focus:outline-none focus:ring-1 ${urlError ? 'focus:ring-red-500' : 'focus:ring-blue-500'}`}
            />
            <button
              type="button"
              onClick={handleShortenUrl}
              disabled={shorteningUrl || !urlToShorten.trim()}
              className={`px-4 py-2 ${shorteningUrl ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'} text-white rounded-r-md flex items-center`}
            >
              {shorteningUrl ? (
                <span>Shortening...</span>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                  Shorten
                </>
              )}
            </button>
          </div>
          
          {/* URL Error Message */}
          {urlError && (
            <div className="mt-1 text-sm text-red-600">
              <svg className="inline-block w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {urlError}
            </div>
          )}
          
          {/* Help Text */}
          {!urlError && (
            <div className="mt-1 text-xs text-gray-500">
              Valid formats: example.com, subdomain.example.co.uk, https://www.example.org
            </div>
          )}
          
          {/* Shortened URLs */}
          {shortenedUrls.length > 0 && (
            <div className="mt-3 p-2 border border-gray-200 rounded-md bg-gray-50">
              <h4 className="text-xs font-medium text-gray-700 mb-2">Shortened URLs:</h4>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {shortenedUrls.map((item, index) => (
                  <div key={index} className="flex justify-between items-center text-sm p-2 bg-white rounded border border-gray-100">
                    <div className="overflow-hidden">
                      <div className="truncate text-xs text-gray-500">{item.original}</div>
                      <div className="text-blue-600 font-medium">{item.shortened}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => insertShortUrl(item.shortened)}
                      className="ml-2 px-2 py-1 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 text-xs"
                    >
                      Insert
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {/* Evergreen Content Recycling */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Evergreen Content
            </label>
            <div className="relative inline-block w-10 mr-2 align-middle select-none">
              <input 
                type="checkbox"
                id="evergreen-toggle"
                checked={formState.is_evergreen || false}
                onChange={(e) => {
                  setFormState({
                    ...formState,
                    is_evergreen: e.target.checked,
                    // Initialize recycling settings with defaults when enabled
                    recycling_settings: e.target.checked ? {
                      interval: 30, // Default: recycle every 30 days
                      max_recycles: 0, // Default: unlimited recycling
                      use_best_time: true // Default: use best time recommendations
                    } : undefined
                  });
                }}
                className="sr-only"
              />
              <label 
                htmlFor="evergreen-toggle"
                className={`block overflow-hidden h-6 rounded-full cursor-pointer ${formState.is_evergreen ? 'bg-green-500' : 'bg-gray-300'}`}
              >
                <span className={`block h-6 w-6 rounded-full bg-white shadow transform transition-transform ${formState.is_evergreen ? 'translate-x-4' : 'translate-x-0'}`}></span>
              </label>
            </div>
          </div>
          
          <div className="text-sm text-gray-500 mb-2">
            Mark this as evergreen content that can be automatically recycled in the future.
          </div>
          
          {formState.is_evergreen && (
            <div className="mt-3 p-3 border border-gray-200 rounded-md bg-white shadow-sm">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {/* Recycling Interval */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Recycle every</label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      min="1"
                      max="365"
                      value={formState.recycling_settings?.interval || 30}
                      onChange={(e) => setFormState({
                        ...formState,
                        recycling_settings: {
                          ...formState.recycling_settings!,
                          interval: parseInt(e.target.value) || 30
                        }
                      })}
                      className="w-16 mr-2 px-2 py-1 border border-gray-300 rounded text-sm"
                    />
                    <span className="text-sm">days</span>
                  </div>
                </div>
                
                {/* Max recycles */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Maximum recycles</label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={formState.recycling_settings?.max_recycles || 0}
                      onChange={(e) => setFormState({
                        ...formState,
                        recycling_settings: {
                          ...formState.recycling_settings!,
                          max_recycles: parseInt(e.target.value) || 0
                        }
                      })}
                      className="w-16 mr-2 px-2 py-1 border border-gray-300 rounded text-sm"
                    />
                    <span className="text-sm">(0 = unlimited)</span>
                  </div>
                </div>
              </div>
              
              {/* Use best time option */}
              <div className="mt-4 flex items-center">
                <input
                  type="checkbox"
                  id="use-best-time"
                  checked={formState.recycling_settings?.use_best_time || false}
                  onChange={(e) => setFormState({
                    ...formState, 
                    recycling_settings: {
                      ...formState.recycling_settings!,
                      use_best_time: e.target.checked
                    }
                  })}
                  className="mr-2"
                />
                <label htmlFor="use-best-time" className="text-sm">Use best time recommendations when recycling</label>
              </div>
              
              {/* Slightly randomize content option */}
              <div className="mt-2 flex items-center">
                <input
                  type="checkbox"
                  id="randomize-content"
                  checked={formState.recycling_settings?.randomize_content || false}
                  onChange={(e) => setFormState({
                    ...formState, 
                    recycling_settings: {
                      ...formState.recycling_settings!,
                      randomize_content: e.target.checked
                    }
                  })}
                  className="mr-2"
                />
                <label htmlFor="randomize-content" className="text-sm">Slightly randomize content each time it's recycled</label>
              </div>
            </div>
          )}
        </div>
        
        {/* Main Content */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Post Content
            </label>
            <div className="flex space-x-2">
              {/* Claude AI Content Generation Button */}
              <button
                type="button"
                onClick={handleOpenClaudeModal}
                className="px-3 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 flex items-center"
                disabled={isLoading}
              >
                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
                Generate with AI
              </button>
              <button
                type="button"
                onClick={() => setShowPlatformPreviews(!showPlatformPreviews)}
                className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded hover:bg-gray-200"
              >
                {showPlatformPreviews ? 'Hide Previews' : 'Show Platform Previews'}
              </button>
              {/* Basic generate button removed */}
            </div>
          </div>
          
          {/* Common content editor */}
          {!showPlatformPreviews && (
            <>
              {/* Formatting toolbar */}
              <div className="mb-4">
                <div className="flex space-x-2 mb-4">
                  <button
                    type="button"
                    onClick={() => applyFormatting('bold')}
                    className="p-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100"
                    title="Bold"
                  >
                    <span className="font-bold">B</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => applyFormatting('italic')}
                    className="p-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100"
                    title="Italic"
                  >
                    <span className="italic">I</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => applyFormatting('hashtag')}
                    className="p-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100"
                    title="Add Hashtag"
                  >
                    #
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowHashtagModal(true)}
                    className="p-2 border border-gray-300 rounded text-blue-700 hover:bg-blue-50"
                    title="Hashtag Collections"
                  >
                    # Collections
                  </button>
                  <button 
                    type="button"
                    onClick={handleOpenClaudeModal}
                    className="p-2 px-3 bg-purple-600 text-white rounded hover:bg-purple-700 flex items-center"
                    disabled={isLoading}
                  >
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd"></path>
                    </svg>
                    Generate with AI
                  </button>
                  {/* Legacy Basic Generate button removed */}
                </div>
                
                <textarea
                  ref={commonTextareaRef}
                  value={formState.content}
                  onChange={(e) => setFormState({ ...formState, content: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-b-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="What would you like to share?"
                ></textarea>
                
                <div className="flex justify-end mt-1">
                  <span className="text-xs text-gray-500">
                    {formState.content.length} characters
                  </span>
                </div>
              </div>
            </>
          )}
          
          {/* Platform-specific previews */}
          {showPlatformPreviews && (
            <div className="border border-gray-200 rounded-md">
              {/* Platform tabs */}
              <div className="flex border-b border-gray-200 bg-gray-50 rounded-t-md overflow-x-auto">
                {formState.platforms.length > 0 ? (
                  formState.platforms.map((platform) => (
                    <button
                      key={platform}
                      onClick={() => setActivePreviewPlatform(platform)}
                      className={`px-4 py-2 text-sm font-medium ${activePreviewPlatform === platform ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      <span className="capitalize">{platform}</span>
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-2 text-sm text-gray-500">
                    Select platforms above to enable previews
                  </div>
                )}
              </div>
              
              {/* Platform content */}
              <div className="p-4">
                {formState.platforms.length > 0 && activePreviewPlatform ? (
                  <div>
                    <div className="mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {activePreviewPlatform.charAt(0).toUpperCase() + activePreviewPlatform.slice(1)} Content
                      </label>
                      
                      {/* Platform-specific formatting toolbar */}
                      <div className="flex flex-wrap items-center justify-between mb-4">
                        <button
                          type="button"
                          onClick={() => applyFormatting('bold')}
                          className="p-1 mr-1 hover:bg-gray-200 rounded"
                          title="Bold"
                        >
                          <span className="font-bold text-sm">B</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => applyFormatting('italic')}
                          className="p-1 mr-1 hover:bg-gray-200 rounded"
                          title="Italic"
                        >
                          <span className="italic text-sm">I</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => applyFormatting('hashtag')}
                          className="p-1 mr-1 hover:bg-gray-200 rounded"
                          title="Add Hashtag"
                        >
                          <span className="text-sm">#</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowHashtagModal(true)}
                          className="p-1 mr-1 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded"
                          title="Hashtag Collections"
                        >
                          <span className="text-sm">#Collections</span>
                        </button>
                        <span className="text-xs text-gray-500 ml-2">Format selected text</span>
                      </div>
                      
                      <textarea
                        ref={platformTextareaRef}
                        value={platformSpecificContent[activePreviewPlatform] || formState.content}
                        onChange={(e) => setPlatformSpecificContent({
                          ...platformSpecificContent,
                          [activePreviewPlatform]: e.target.value
                        })}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-b-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={`Customize your ${activePreviewPlatform} content...`}
                      ></textarea>
                    </div>
                    
                    {/* Platform preview */}
                    <div className="border border-gray-200 rounded-md p-3 bg-gray-50">
                      <h5 className="text-sm font-medium mb-2">Preview</h5>
                      <div className="border rounded-md p-3 bg-white">
                        <div className="flex items-center mb-2">
                          <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center mr-2">
                            <span className="font-bold text-xs text-gray-600">{activePreviewPlatform.charAt(0).toUpperCase()}</span>
                          </div>
                          <span className="font-medium text-sm">Your {activePreviewPlatform.charAt(0).toUpperCase() + activePreviewPlatform.slice(1)} Page</span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">
                          {platformSpecificContent[activePreviewPlatform] || formState.content}
                        </p>
                      </div>
                      
                      {/* Character limits and platform-specific tips */}
                      <div className="mt-2 flex justify-between items-center text-xs text-gray-500">
                        <span>{getPlatformCharacterLimit(activePreviewPlatform, platformSpecificContent[activePreviewPlatform] || formState.content)}</span>
                        <button
                          type="button"
                          onClick={() => handleOpenClaudeModal()}
                          className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded hover:bg-blue-100 mb-2"
                        >
                          Optimize for {activePreviewPlatform}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    {formState.platforms.length === 0 ? 
                      'Select at least one platform above to create platform-specific content' : 
                      'Select a platform tab to customize content'}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <button 
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button 
            onClick={() => handleSubmit()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Save Post
          </button>
        </div>
      </div>
      {/* Claude Content Generation Modal */}
      {showClaudeModal && (
        <ClaudeContentModal
          platforms={formState.platforms}
          onClose={() => setShowClaudeModal(false)}
          onContentGenerated={handleClaudeContentGenerated}
          existingContent={showPlatformPreviews && activePreviewPlatform ? 
            platformSpecificContent[activePreviewPlatform || ''] : 
            formState.content}
        />
      )}
      
      {/* Hashtag Collection Modal */}
      {showHashtagModal && (
        <HashtagCollectionModal
          isOpen={showHashtagModal}
          onClose={() => setShowHashtagModal(false)}
          onSelectHashtags={(hashtags) => {
            // Add selected hashtags to the current post
            const currentHashtags = formState.hashtags || [];
            // Filter out duplicates by only adding hashtags not already in the array
            const newHashtags = [...currentHashtags];
            
            // Add only unique hashtags
            hashtags.forEach(tag => {
              if (!newHashtags.includes(tag)) {
                newHashtags.push(tag);
              }
            });
            
            setFormState({
              ...formState,
              hashtags: newHashtags
            });
            toast.success(`${hashtags.length} hashtags added to your post`);
          }}
          initialPlatform={activePreviewPlatform || formState.platforms[0]}
        />
      )}
    </div>
  );
};

export default PostComposer;
